<template>
  <button type="button" class="scopec" @click="open = true">
    <span class="scopec__label">{{ label }}</span>
    <el-icon class="scopec__caret"><ArrowDown /></el-icon>
  </button>

  <ResponsiveDialog v-model="open" title="범위" width="420px">
    <ul class="scopes" aria-label="조회 범위">
      <li v-for="opt in options" :key="opt.value" class="scopes__li">
        <!-- role="radio" 를 쓰지 않는다. 그 역할은 radiogroup 이 radio 를 직접
             소유해야 하는데, 여기 <li> 안에는 선택된 행에서만 펼쳐지는
             el-select 가 함께 들어 있어 그 구조를 만들 수 없다.
             업무 보드의 범위 탭(Tasks.vue)과 같은 aria-pressed 로 맞춘다. -->
        <button
          type="button"
          class="scopes__item"
          :aria-pressed="isActive(opt)"
          :class="{ 'is-active': isActive(opt) || subOpen === opt.value }"
          @click="pick(opt)"
        >
          <span class="scopes__radio" :class="{ 'is-on': isActive(opt) }" />
          <span class="scopes__text">
            <span class="scopes__name">{{ opt.label }}</span>
            <span v-if="opt.hint" class="scopes__hint">{{ opt.hint }}</span>
          </span>
          <!-- 하위 선택이 있는 항목의 표식. 시트를 벗어나 어디로 가는 게 아니라
               **이 행 아래**가 펼쳐지므로, 고를 때 90도 돌려 아래를 가리킨다.
               오른쪽 화살표로 고정해 두면 다음 화면이 열릴 것처럼 읽힌다. -->
          <el-icon v-if="opt.value === 'user' || opt.value === 'department'" class="scopes__chev">
            <ArrowRight />
          </el-icon>
        </button>

        <!-- 부서/인원 선택 -->
        <div v-if="opt.value === 'department' && subOpen === 'department'" class="scopes__sub">
          <el-select
            :model-value="deptId"
            placeholder="부서 선택"
            size="default"
            style="width: 100%"
            @update:model-value="(v) => emitChange({ scope: 'department', deptId: v })"
          >
            <el-option v-for="d in departments" :key="d.id" :label="d.name" :value="d.id" />
          </el-select>
        </div>
        <div v-if="opt.value === 'user' && subOpen === 'user'" class="scopes__sub">
          <el-select
            :model-value="userId"
            placeholder="구성원 선택"
            filterable
            size="default"
            style="width: 100%"
            @update:model-value="(v) => emitChange({ scope: 'user', userId: v })"
          >
            <el-option v-for="u in users" :key="u.id" :label="u.nickname" :value="u.id" />
          </el-select>
        </div>
      </li>
    </ul>
  </ResponsiveDialog>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { ArrowDown, ArrowRight } from '@element-plus/icons-vue'
import ResponsiveDialog from '@/components/dialogs/ResponsiveDialog.vue'

/**
 * 캘린더의 범위 선택.
 *
 * 기존 캘린더 헤더는 "내 일정만" 스위치 + 사용자 셀렉트로 150~190px 를 먹었다.
 * 칩 하나(44px)로 줄이고 실제 선택은 바텀시트에서 한다. 모바일 세로 공간이
 * 캘린더에서 가장 비싼 자원이다.
 *
 * 부서 조회를 1급으로 유지한다 — 지금까지 업무 보드에만 있었고 캘린더·홈·보고에는 없었다.
 * (업무 보드는 지금도 자체 pill 탭을 쓴다. 여기로 합칠 수는 있지만 그건 별개 작업이고,
 *  합치기 전까지 "공유한다" 고 적어 두면 읽는 사람이 사실과 다른 지도를 갖게 된다.)
 */
export default {
  name: 'ScopeControl',
  components: { ResponsiveDialog, ArrowDown, ArrowRight },
  props: {
    scope: { type: String, default: 'all' },
    userId: { type: Number, default: null },
    deptId: { type: Number, default: null },
    users: { type: Array, default: () => [] },
    departments: { type: Array, default: () => [] }
  },
  emits: ['change'],
  setup(props, { emit }) {
    const open = ref(false)

    /*
     * hint 는 장식이 아니다. 라벨만으로는 범위가 어디까지인지 알 수 없어
     * 매번 골라 보고 되돌리는 일이 생겼다.
     *
     * **문구는 서버 규칙 그대로여야 한다**(server/middleware/agendaScope.js):
     *   mine       업무는 `assigneeId = 나 OR creatorId = 나` (:90-91) —
     *              내가 만들어 남에게 맡긴 업무도 들어온다. 일정만 소유자 기준(:64).
     *   department `descendantDepartmentIds()` 라 하위 부서까지 포함한다(:98).
     * 틀린 hint 는 없는 것만 못하다 — 없으면 열어 보지만, 틀리면 안 열어 본다.
     *
     * 'unassigned' 항목은 뒀다가 뺐다. allowUnassigned 를 넘기는 사용처가 없었고,
     * 넘긴다 해도 resolveScope(agendaScope.js:23)가 ['all','mine','department','user']
     * 만 받아 조용히 'all' 로 떨어진다 — 고르면 전사가 나오는 항목이 된다.
     */
    const options = computed(() => [
      { value: 'mine', label: '내 것', hint: '내가 담당하거나 내가 만든 것' },
      { value: 'department', label: '우리 부서', hint: '우리 부서와 하위 부서' },
      { value: 'user', label: '특정 인원', hint: '한 사람만 골라서' },
      { value: 'all', label: '전체', hint: '전사 구성원' }
    ])

    const label = computed(() => {
      if (props.scope === 'mine') return '내 것'
      if (props.scope === 'all') return '전체'
      if (props.scope === 'department') {
        const d = props.departments.find((x) => Number(x.id) === Number(props.deptId))
        return d ? d.name : '우리 부서'
      }
      if (props.scope === 'user') {
        const u = props.users.find((x) => Number(x.id) === Number(props.userId))
        return u ? u.nickname : '특정 인원'
      }
      return '전체'
    })

    function isActive(opt) {
      return props.scope === opt.value
    }

    /*
     * 어느 행의 하위 셀렉트가 펼쳐져 있는지. **scope 와 별개로** 들고 있어야 한다.
     *
     * 예전에는 펼침 조건이 `scope === 'user'` 여서, 셀렉트를 보려면 먼저
     * scope 를 'user' 로 바꿔야 했다. 그 emit 은 userId 없이 나가고
     * 서버(agendaScope.resolveScope)는 userId 가 비면 필터를 아예 걸지 않는다 —
     * "특정 인원" 을 골랐는데 잠깐 전사가 뜨고, 트리거 라벨도 그동안 거짓말을 한다.
     * 이제 사람이 정해질 때 scope 와 userId 를 **함께** 내보낸다.
     */
    const subOpen = ref(null)

    watch(open, (v) => {
      // 시트를 열 때마다 현재 스코프에 맞춰 초기화한다. 지난번에 펼쳐 둔 행이
      // 다음에 그대로 남아 있으면 지금 무엇이 선택돼 있는지 읽기 어렵다.
      if (v) subOpen.value = props.scope === 'user' || props.scope === 'department' ? props.scope : null
    })

    function emitChange(payload) {
      emit('change', payload)
      // 하위 선택이 필요한 항목은 시트를 열어둔다
      if (payload.scope !== 'department' && payload.scope !== 'user') open.value = false
      else if (payload.deptId || payload.userId) open.value = false
    }

    function pick(opt) {
      if (opt.value === 'department' || opt.value === 'user') {
        subOpen.value = opt.value
        // 이미 값이 있으면(다시 고른 경우) 그대로 확정한다. 없으면 셀렉트만 펼치고
        // 아무것도 내보내지 않는다 — 위 주석 참고.
        const id = opt.value === 'user' ? props.userId : props.deptId
        if (id) emitChange(opt.value === 'user' ? { scope: 'user', userId: id } : { scope: 'department', deptId: id })
        return
      }
      subOpen.value = null
      emitChange({ scope: opt.value })
    }

    return { open, subOpen, options, label, isActive, pick, emitChange }
  }
}
</script>

<style scoped>
/*
 * 트리거는 캘린더 툴바에서 .cal__today · .cal__filter 와 한 줄에 선다.
 * 세 버튼의 높이(34) · 테두리(--color-border-strong) · 모서리(--r-md)를
 * 맞춘다. 예전에는 이것만 --color-border 에 --r-full 이라 같은 줄에서
 * 혼자 알약 모양이었고, 그래서 다른 종류의 컨트롤처럼 보였다.
 */
.scopec {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  height: 34px;
  padding: 0 var(--sp-2) 0 var(--sp-3);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--r-md);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: inherit;
  font-size: var(--fs-md);
  font-weight: var(--fw-medium);
  cursor: pointer;
  /* 좁은 툴바에서는 줄어들어도 된다 — 라벨이 말줄임으로 접힌다.
     flex-shrink:0 을 주면 390px 화면에서 툴바가 가로로 넘친다. */
  max-width: 130px;
  min-width: 0;
  -webkit-tap-highlight-color: transparent;
}
.scopec:hover { background: var(--color-surface-2); }
.scopec:active { background: var(--color-border); }

.scopec__label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.scopec__caret { font-size: var(--ic-xs); color: var(--color-text-muted); flex-shrink: 0; }

/* ---------- 시트 ---------- */
.scopes { list-style: none; margin: 0; padding: 0; }
.scopes__li + .scopes__li { margin-top: 2px; }

.scopes__item {
  width: 100%;
  min-height: 56px;
  display: flex;
  align-items: center;
  gap: var(--sp-3);
  padding: var(--sp-2) var(--sp-3);
  border: 0;
  border-radius: var(--r-md);
  background: none;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
}
.scopes__item:hover { background: var(--color-surface-2); }
.scopes__item:active { background: var(--color-border); }
/* 선택된 행은 배경으로 말한다. 라디오 점 하나(18px)는 시트를 훑을 때
   눈에 걸리지 않아서, 어느 범위로 보고 있었는지 매번 찾아야 했다. */
.scopes__item.is-active { background: var(--color-primary-soft); }
.scopes__item.is-active:hover { background: var(--color-primary-soft); }

.scopes__radio {
  width: 18px;
  height: 18px;
  border: 2px solid var(--color-border-strong);
  border-radius: var(--r-full);
  background: var(--color-surface);
  flex-shrink: 0;
}
.scopes__radio.is-on {
  border-color: var(--color-primary);
  border-width: 5px;
}

.scopes__text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 1px; }
.scopes__name { font-size: var(--fs-md); color: var(--color-text); font-weight: var(--fw-medium); }
.is-active .scopes__name { color: var(--color-primary-strong); }
.scopes__hint { font-size: var(--fs-xs); color: var(--color-text-subtle); }
.is-active .scopes__hint { color: var(--color-primary-strong); opacity: 0.75; }

.scopes__chev {
  color: var(--color-text-subtle);
  flex-shrink: 0;
  transition: transform var(--dur-fast) var(--ease);
}
.is-active .scopes__chev { transform: rotate(90deg); color: var(--color-primary-strong); }

/* 하위 셀렉트는 선택된 행 **아래에서만** 펼쳐진다.
   왼쪽 여백은 행의 좌패딩(12) + 라디오(18) + 간격(12) = 42px 로 맞춰
   위 행의 라벨과 같은 x 에서 시작하게 한다. */
.scopes__sub { padding: var(--sp-2) var(--sp-3) var(--sp-3) 42px; }

@media (max-width: 767px) { /* --bp-mobile-max */
  /* 캘린더 툴바에서 손가락으로 누르는 버튼이다. 모양도 이때는 알약으로
     바꾼다 — .cal__today · .cal__filter 가 같은 조건에서 그렇게 한다. */
  .scopec {
    height: var(--tap-min);
    border-radius: var(--r-full);
  }
}
</style>
